import { DEV_BYPASS, apiToken, reauthenticate } from './auth';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const EXPIRED = 'Your sign-in has expired — taking you to sign in again.';

/**
 * Attaches the Entra access token. When the sign-in cannot be renewed the
 * request does NOT go out without one — that is what produced "A bearer token
 * is required" and left the app stuck until a sign-out — the interactive
 * sign-in starts instead (auth.ts apiToken) and the call fails with a 401.
 */
async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  if (DEV_BYPASS) return {};
  const token = await apiToken(forceRefresh);
  if (!token) throw new ApiError(401, EXPIRED);
  return { Authorization: `Bearer ${token}` };
}

/** fetch with the token, retried once with a renewed token if the API rejects it. */
async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const send = async (forceRefresh: boolean) =>
    fetch(url, { ...init, headers: { ...(init.headers as Record<string, string> | undefined), ...(await authHeaders(forceRefresh)) } });
  let response = await send(false);
  if (response.status === 401 && !DEV_BYPASS) {
    response = await send(true);
    if (response.status === 401) {
      reauthenticate();
      throw new ApiError(401, EXPIRED);
    }
  }
  return response;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await authedFetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    if (err instanceof ApiError) throw err;
    /* fetch only rejects when the request never completed — the API is down,
       or the dev proxy has nothing to forward to. Status 0 marks "never
       reached the server", which callers treat differently from a real HTTP
       error. */
    throw new ApiError(0, 'Cannot reach the API. Is the server running?');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();

  /* Not every non-2xx response is JSON — a dev-proxy failure or a platform
     error page is plain text or empty, and letting JSON.parse throw here
     would replace a useful status with a SyntaxError. */
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const problem = payload as { error?: string; details?: unknown } | null;
    const fallback =
      response.status >= 500
        ? `The server returned an error (${response.status}). It may be down or unable to reach the database.`
        : `Request failed (${response.status})`;
    throw new ApiError(response.status, problem?.error ?? fallback, problem?.details);
  }

  return payload as T;
}

/**
 * Fetches a non-JSON response as text.
 *
 * The SOP viewer needs this: it is a server-rendered HTML page behind the same
 * bearer token as everything else, and a plain `<a href>` in a new tab carries
 * no Authorization header. So it is fetched here and handed to the new tab as a
 * blob instead.
 */
async function requestText(path: string): Promise<string> {
  let response: Response;
  try {
    response = await authedFetch(`${BASE}${path}`);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Cannot reach the API. Is the server running?');
  }
  const text = await response.text();
  if (!response.ok) throw new ApiError(response.status, `Request failed (${response.status})`);
  return text;
}

/**
 * Fetches a binary response as a blob.
 *
 * Same problem as `requestText`, one step further along: a job description
 * document is served from behind the bearer token, and a plain `<a href>` or a
 * `<embed src>` carries no Authorization header — it would 401. So the bytes
 * are fetched here and handed to the browser as an object URL.
 */
async function requestBlob(path: string): Promise<Blob> {
  let response: Response;
  try {
    response = await authedFetch(`${BASE}${path}`);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, 'Cannot reach the API. Is the server running?');
  }
  if (!response.ok) {
    /* An error response IS json — it came through the same handler as
       everything else — so read the message rather than reporting a status. */
    const problem = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, problem?.error ?? `Request failed (${response.status})`);
  }
  return response.blob();
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  getText: (path: string) => requestText(path),
  getBlob: (path: string) => requestBlob(path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  del: (path: string) => request<void>('DELETE', path),
};
